/**
 * Página principal: tablero de embarque con formulario de reserva,
 * lista de formularios, barra de búsqueda y estadísticas.
 * Requiere sesión activa (el middleware redirige a /login si no hay).
 */
import ReservationForm, { type EditableFormItem } from '@/features/forms/components/ReservationForm';
import FormList, { type FormItem } from '@/features/forms/components/FormList';
import SearchBar, { type SearchFilters, EMPTY_FILTERS } from '@/features/forms/components/SearchBar';
import StatsBar, { type FormTotals } from '@/features/forms/components/StatsBar';
import { useAuth } from '@/features/auth/AuthProvider';
import { useEmbarkations } from '@/features/embarkations/hooks/useEmbarkations';
import { formsService } from '@/features/forms/services/formsService';
import { storage } from '@/services/storage';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState, useCallback } from 'react';
import { LogOut, Settings, Trash2, UserCircle, X } from 'lucide-react';
import Header from '@/components/Header';
import ConfirmDialog from '@/components/ConfirmDialog';
import dialogStyles from '@/components/ConfirmDialog.module.css';
import styles from './HomePage.module.css';

interface SelectedEmbarkation {
    id: string | number;
    nombre: string;
}

export function HomePage() {
    const navigate = useNavigate();
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [menuVisible, setMenuVisible] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filters, setFilters] = useState<SearchFilters>(EMPTY_FILTERS);
    const [editingItem, setEditingItem] = useState<EditableFormItem | null>(null);
    const [totals, setTotals] = useState<FormTotals>({ vehiculos: 0, motos: 0, camiones: 0, autobuses: 0, adultos: 0, niños: 0, bebes: 0, remolques: 0 });
    const [checkedTotals, setCheckedTotals] = useState<FormTotals>({ vehiculos: 0, motos: 0, camiones: 0, autobuses: 0, adultos: 0, niños: 0, bebes: 0, remolques: 0 });
    /** Botón modo “plegar”: `true` = todas las fichas plegadas; `false` = desplegadas. */
    const [expandAll, setExpandAll] = useState(false);
    const [selectedEmbarkation, setSelectedEmbarkationRaw] = useState<SelectedEmbarkation>({ id: '', nombre: '' });
    const [headerContent, setHeaderContent] = useState<React.ReactNode>(null);
    const [markerModalOpen, setMarkerModalOpen] = useState(false);
    const [deleteEmbarkDialogOpen, setDeleteEmbarkDialogOpen] = useState(false);

    const [sessionChecked, setSessionChecked] = useState(false);
    const { user, checkSession, logout } = useAuth();
    const {
        embarkations,
        fetchEmbarkations,
        getInitialEmbarkation,
        selectedEmbarkationStorageKey,
    } = useEmbarkations();

    const setSelectedEmbarkation = (value: SelectedEmbarkation | ((prev: SelectedEmbarkation) => SelectedEmbarkation)) => {
        const row = typeof value === 'function' ? value(selectedEmbarkation) : value;
        setSelectedEmbarkationRaw(row);
        if (row?.id && row.id !== '') {
            storage.setItem(selectedEmbarkationStorageKey, row.id.toString());
        }
    };

    const handleSaveSuccess = () => {
        // La lista se actualiza por WebSocket (eventos create/update); no recargamos todo.
        setEditingItem(null);
    };

    const handleEditItem = (item: FormItem) => {
        setEditingItem(item as EditableFormItem);
        if (item.datos?.embarqueId) {
            const row = embarkations.find(e => e.id.toString() === item.datos.embarqueId.toString());
            if (row) setSelectedEmbarkation(row as SelectedEmbarkation);
        }
        scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => setEditingItem(null);

    const handleLogout = async () => {
        await logout();
        setMenuVisible(false);
        navigate('/login', { replace: true });
    };

    const confirmDeleteAllEmbarkationForms = async () => {
        setDeleteEmbarkDialogOpen(false);
        if (selectedEmbarkation.id === '' || selectedEmbarkation.id == null) return;
        const ok = await formsService.deleteAllByEmbarkation(selectedEmbarkation.id);
        if (ok) {
            // El vaciado se refleja por WebSocket (evento bulk_delete).
            setEditingItem(null);
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const u = await checkSession();
            if (cancelled) return;
            if (!u) { navigate('/login', { replace: true }); return; }
            setSessionChecked(true);
            const list = await fetchEmbarkations();
            const initial = getInitialEmbarkation(list);
            if (initial) setSelectedEmbarkation(initial as SelectedEmbarkation);
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (selectedEmbarkation.id === '' || selectedEmbarkation.id == null) return;
        const row = embarkations.find(e => String(e.id) === String(selectedEmbarkation.id));
        if (!row) return;
        setSelectedEmbarkation(prev => ({
            ...prev,
            nombre: row.nombre,
        }));
    }, [embarkations, selectedEmbarkation.id]);

    const handleHeaderContent = useCallback((content: React.ReactNode) => {
        setHeaderContent(content);
    }, []);

    if (!sessionChecked) {
        return <div className={styles.loadingScreen}><div className="spinner" /></div>;
    }

    return (
        <div className={styles.wrapper}>
            <Header
                titleContent={headerContent}
                rightContent={
                    <button
                        type="button"
                        onClick={() => setMenuVisible(true)}
                        className={styles.headerIconButton}
                        aria-label="Menú de usuario"
                    >
                        <UserCircle color="#fff" size={28} />
                    </button>
                }
            />

            <div className={styles.scrollContainer} ref={scrollRef}>
                <div className={styles.contentContainer}>
                    <div className={styles.formWrapper}>
                        <ReservationForm
                            onSaveSuccess={handleSaveSuccess}
                            editData={editingItem}
                            onCancelEdit={handleCancelEdit}
                            selectedEmbarkation={selectedEmbarkation as { id: string; nombre: string }}
                            onEmbarkationChange={setSelectedEmbarkation}
                            embarkations={embarkations}
                            onHeaderContent={handleHeaderContent}
                        />
                    </div>

                    {user &&
                        selectedEmbarkation.id !== '' &&
                        selectedEmbarkation.id != null && (
                        <>
                            <SearchBar
                                value={searchTerm}
                                onChangeText={setSearchTerm}
                                filters={filters}
                                onFiltersChange={setFilters}
                                expandAll={expandAll}
                                onExpandAllChange={setExpandAll}
                                onOpenMarker={() => setMarkerModalOpen(true)}
                            />
                            <FormList
                                searchTerm={searchTerm}
                                filters={filters}
                                onEdit={handleEditItem}
                                embarkationId={selectedEmbarkation.id.toString()}
                                onTotalsChange={setTotals}
                                onCheckedTotalsChange={setCheckedTotals}
                                expandAll={expandAll}
                            />
                        </>
                    )}
                </div>
            </div>

            {markerModalOpen && user && (
                <div
                    className={styles.statsModalOverlay}
                    onClick={() => setMarkerModalOpen(false)}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="stats-modal-title"
                >
                    <div className={styles.statsModalDialog} onClick={e => e.stopPropagation()}>
                        <div className={styles.statsModalHeaderRow}>
                            <h2 id="stats-modal-title" className={styles.statsModalTitle}>
                                Totales
                            </h2>
                            <button
                                type="button"
                                className={styles.statsModalClose}
                                onClick={() => setMarkerModalOpen(false)}
                                aria-label="Cerrar"
                            >
                                <X size={18} color="currentColor" strokeWidth={2.25} />
                            </button>
                        </div>
                        <div className={styles.statsModalBody}>
                            <StatsBar totals={totals} checkedTotals={checkedTotals} embedded />
                        </div>
                    </div>
                </div>
            )}

            {menuVisible && (
                <div className={styles.modalOverlay} onClick={() => setMenuVisible(false)}>
                    <div className={styles.dropdownMenu} onClick={e => e.stopPropagation()}>
                        <div className={styles.menuHeaderRow}>
                            <span className={styles.menuUsername}>{user?.name}</span>
                            <button className={styles.menuLogoutIcon} onClick={handleLogout} aria-label="Cerrar sesión">
                                <LogOut size={20} color="#ff4444" />
                            </button>
                        </div>

                        <div className={styles.divider} />

                        {user?.rol === 'admin' && (
                            <button className={styles.menuItem} onClick={() => { setMenuVisible(false); navigate('/admin'); }}>
                                <Settings size={20} color="#333" />
                                <span className={styles.menuItemText}>Configuración</span>
                            </button>
                        )}

                        <button
                            type="button"
                            className={styles.menuItem}
                            disabled={selectedEmbarkation.id === '' || selectedEmbarkation.id == null}
                            onClick={() => {
                                setMenuVisible(false);
                                setDeleteEmbarkDialogOpen(true);
                            }}
                        >
                            <Trash2 size={20} color="#ff4444" />
                            <span className={`${styles.menuItemText} ${styles.menuItemTextDanger}`}>Borrar embarque</span>
                        </button>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={deleteEmbarkDialogOpen}
                title="Borrar embarque"
                onCancel={() => setDeleteEmbarkDialogOpen(false)}
                onConfirm={confirmDeleteAllEmbarkationForms}
                confirmLabel="Borrar"
                cancelLabel="Cancelar"
            >
                <p>
                    ¿Estás seguro de que quieres borrar todos los formularios del embarque{' '}
                    <strong className={dialogStyles.modalStrong}>{selectedEmbarkation.nombre || 'actual'}</strong>? Esta
                    acción no se puede deshacer.
                </p>
            </ConfirmDialog>
        </div>
    );
}
